import { z } from "zod";
import { useTRPC } from "../../trpc";
import { useCallback, useEffect, useMemo } from "react";
import { Button, Checkbox, Loader, PasswordInput, Stack, Text, TextInput, Title } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useAllParams } from "../../hooks/router";
import { FormattedMessage, useIntl } from "react-intl";
import { nameSchema, passwordSchemaStrength } from "../../utils/schema";
import { PasswordStrength } from "../../components/PasswordStrength";
import { zod4Resolver } from "mantine-form-zod-resolver";
import type { Locale } from "@project/common";
import { getInitialLocale } from "@project/common";
import { captureEvent } from "../../utils/posthog";
import { useSetUser } from "../../hooks/user";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Event } from "../../constants/events";
import { useNavigateUserAuth } from "./hooks/navigate";
import { TermsConditionsCheckbox } from "./components/TermsConditionsCheckbox";

const initialValues = {
  name: "",
  password: "",
  marketingConsent: false,
  locale: getInitialLocale(),
};

export function AcceptInvitation() {
  const navigate = useNavigateUserAuth();
  const intl = useIntl();
  const trpc = useTRPC();
  const setUser = useSetUser();
  const { token = "" } = useAllParams();

  const { data: isExpired, isFetching } = useQuery(trpc.checkIfExpired.queryOptions({
    token,
  }));

  const { mutate, isPending } = useMutation(
    trpc.acceptUserInvitation.mutationOptions({
      onSuccess: (user) => {
        setUser(user);
        navigate(user);
      },
    }),
  );

  const validate = useMemo(() => zod4Resolver(
    z.object({
      name: nameSchema(intl),
      locale: z.string(),
      marketingConsent: z.boolean(),
      password: passwordSchemaStrength(intl),
    }),
  ), [intl]);

  const { onSubmit, getInputProps, isValid, setFieldValue, values } = useForm({
    validateInputOnBlur: true,
    initialValues,
    validate,
  });

  useEffect(() => {
    setFieldValue("locale", intl.locale as Locale);
  }, [setFieldValue, intl]);

  const handleSubmit = useCallback((form: typeof initialValues) => {
    captureEvent(Event.AuthRequestVerificationEmail);
    mutate({
      token,
      ...form,
    });
  }, [mutate, token]);

  return (
    <>
      {isFetching && (
        <Stack gap="1rem">
          <Loader size="lg" />
          <Text size="xl">
            <FormattedMessage id="text.verifyingRequest" defaultMessage="Verifying request" />
          </Text>
        </Stack>
      )}
      {isExpired && (
        <Stack gap="1rem">
          <Title size="h3">
            <FormattedMessage id="title.invalidRequest" defaultMessage="Invalid request" />
          </Title>
          <Text size="xl">
            <FormattedMessage
              id="text.expiredOrInvalidLink"
              defaultMessage="Link expired or is invalid. Please ask administrator for new invitation."
            />
          </Text>
        </Stack>
      )}
      {!isExpired && (
        <Stack>
          <form onSubmit={onSubmit(handleSubmit)}>
            <Stack gap="1rem">
              <Title size="h3">
                <FormattedMessage id="title.acceptInvitation" defaultMessage="Accept invitation" />
              </Title>
              <TextInput
                label={<FormattedMessage id="label.name" defaultMessage="Name" />}
                required
                {...getInputProps("name")}
              />
              <PasswordInput
                required
                autoComplete="new-password"
                label={<FormattedMessage id="label.password" defaultMessage="Password" />}
                {...getInputProps("password")}
              />
              <PasswordStrength {...values} />
              <TermsConditionsCheckbox
                locale={values.locale}
                {...getInputProps("termsAccepted", { type: "checkbox" })}
              />

              <Checkbox
                pt="xs"
                label={(
                  <FormattedMessage
                    id="label.marketingConsent"
                    defaultMessage="I would like to receive occasional email updates and special offers for products, services, and events."
                  />
                )}
                {...getInputProps("marketingConsent")}
              />
              <Button type="submit" fullWidth loading={isPending} disabled={!isValid()} variant="primary">
                <FormattedMessage id="button.acceptInvitation" defaultMessage="Accept invitation" />
              </Button>
            </Stack>
          </form>
        </Stack>
      )}
    </>
  );
}
